import type { TypesenseCollectionSchema } from '../../types/typesense';
import { TYPESENSE_SRP_INCLUDE_FIELDS } from './typesense-srp-include-fields';

/**
 * Profile/ranking parity fields added to the companies collection so the
 * best-companies rankings and company profile page can read from Typesense.
 * All optional (added to a populated collection via schema migration).
 * Shared with companyProfileFieldsMigration to avoid drift.
 */
export const TYPESENSE_COMPANY_PROFILE_FIELDS = [
  // Employer score + sub-scores (employerScore must stay indexed → sortable for rankings)
  { name: 'employerScore', type: 'float', optional: true },
  { name: 'payScore', type: 'float', optional: true },
  { name: 'flexibilityScore', type: 'float', optional: true },
  { name: 'stabilityScore', type: 'float', optional: true },
  { name: 'growthScore', type: 'float', optional: true },
  { name: 'benefitsScore', type: 'float', optional: true },
  { name: 'transparencyScore', type: 'float', optional: true },
  // Derived signals
  { name: 'hiringTrend', type: 'string', facet: true, optional: true },
  { name: 'remotenessRemotePct', type: 'int32', optional: true },
  { name: 'remotenessHybridPct', type: 'int32', optional: true },
  { name: 'recentJobsPerMonth', type: 'float', optional: true },
  { name: 'topRoles', type: 'string[]', optional: true, index: false },
  { name: 'companyAgeYears', type: 'float', optional: true },
  // Salary stats
  { name: 'medianSalaryMin', type: 'int32', optional: true },
  { name: 'medianSalaryMax', type: 'int32', optional: true },
  { name: 'salaryPercentile25', type: 'int32', optional: true },
  { name: 'salaryPercentile75', type: 'int32', optional: true },
  { name: 'salarySampleSize', type: 'int32', optional: true },
  // Trilingual industry (industry === industryEn)
  { name: 'industryFi', type: 'string', facet: true, optional: true },
  { name: 'industrySv', type: 'string', facet: true, optional: true },
  // Registry
  { name: 'registryCountry', type: 'string', facet: true, optional: true },
  { name: 'registryBusinessId', type: 'string', optional: true, index: false },
  { name: 'registryRegistrationDate', type: 'string', optional: true, index: false },
  { name: 'registryStatus', type: 'string', optional: true, index: false },
  // JSON-stringified CompanyRegistryLifecycle (utils/company/company-financials.ts).
  { name: 'registryLifecycle', type: 'string', optional: true, index: false },
  // Profile display
  { name: 'brandColour', type: 'string', optional: true, index: false },
  { name: 'heroImageUrl', type: 'string', optional: true, index: false },
  // Financials
  { name: 'revenue', type: 'float', optional: true, index: false },
  { name: 'profit', type: 'float', optional: true, index: false },
  { name: 'profitMargin', type: 'float', optional: true, index: false },
  // JSON-stringified CompanyFinancialYear[] (PRH digital financial statements) and
  // CompanyTaxRecord[] (Verohallinto public tax data), newest first, at most 5 years each.
  { name: 'companyFinancials', type: 'string', optional: true, index: false },
  { name: 'companyTaxRecords', type: 'string', optional: true, index: false },
  // Social
  { name: 'linkedinUrl', type: 'string', optional: true, index: false },
  { name: 'facebookUrl', type: 'string', optional: true, index: false },
  { name: 'instagramUrl', type: 'string', optional: true, index: false },
  { name: 'tiktokUrl', type: 'string', optional: true, index: false },
  { name: 'youtubeUrl', type: 'string', optional: true, index: false },
  { name: 'contactEmail', type: 'string', optional: true, index: false },
  { name: 'contactPhone', type: 'string', optional: true, index: false },
  { name: 'employeesPageUrl', type: 'string', optional: true, index: false },
  // Distributions / monthly history stored as JSON strings (parsed on read)
  { name: 'roleDistribution', type: 'string', optional: true, index: false },
  { name: 'roleLevelDistribution', type: 'string', optional: true, index: false },
  { name: 'locationDistribution', type: 'string', optional: true, index: false },
  { name: 'remotenessDistribution', type: 'string', optional: true, index: false },
  { name: 'languageRequirements', type: 'string', optional: true, index: false },
  { name: 'companyMonthlyCounts', type: 'string', optional: true, index: false },
  // JSON-stringified CommunityInsights, grounded synthesis of public web/forum discussion.
  { name: 'communityInsights', type: 'string', optional: true, index: false },
  // JSON-stringified {url,title,domain}[], the company's own pages `about` (and other
  // CompanySiteProfile facts) were sourced from.
  { name: 'siteSources', type: 'string', optional: true, index: false },
  // CompanySiteProfile facts beyond about/benefits-score (see lambda/types.ts). "benefits"
  // is named benefitsText here to avoid colliding with the existing benefitsScore field.
  { name: 'benefitsText', type: 'string', optional: true, index: false },
  { name: 'foundingYear', type: 'int32', optional: true, index: false },
  { name: 'officeLocations', type: 'string', optional: true, index: false },
  { name: 'remotePolicy', type: 'string', optional: true, index: false },
  { name: 'companyValues', type: 'string', optional: true, index: false },
  { name: 'corporateHistory', type: 'string', optional: true, index: false },
  // Drives company-sitemap.xml inclusion and the profile page's robots meta.
  { name: 'hasSufficientContent', type: 'bool', optional: true },
  // Drives the /employers hub's default (unsearched) listing cohort.
  { name: 'hasFinlandJobHistory', type: 'bool', optional: true },
] as const;

export const TYPESENSE_COMPANIES_SCHEMA: TypesenseCollectionSchema = {
  name: 'companies',
  fields: [
    { name: 'id', type: 'string' },
    { name: 'name', type: 'string' },
    { name: 'slug', type: 'string' },
    { name: 'logo_url', type: 'string', optional: true, index: false },
    { name: 'website', type: 'string', optional: true, index: false },
    { name: 'industry', type: 'string', facet: true, optional: true },
    { name: 'size', type: 'string', facet: true, optional: true },
    { name: 'location', type: 'string', facet: true, optional: true },
    { name: 'description', type: 'string', optional: true },
    { name: 'active_job_count', type: 'int32' },
    { name: 'annualRevenueRangeMin', type: 'int32', optional: true, facet: true },
    { name: 'annualRevenueRangeMax', type: 'int32', optional: true, facet: true },
    { name: 'headcountRangeMin', type: 'int32', optional: true, facet: true },
    { name: 'headcountRangeMax', type: 'int32', optional: true, facet: true },
    { name: 'awardsAndCertifications', type: 'string[]', optional: true, facet: true },
    // TEC-482 follow-up: gazetteer-resolved location(s), companyLocations is JSON-stringified
    // display/audit data (unindexed, same treatment as siteSources); regionTags is the real
    // facet, same name/shape as the jobs collection's field.
    { name: 'companyLocations', type: 'string', optional: true, index: false },
    { name: 'regionTags', type: 'string[]', optional: true, facet: true },
    ...TYPESENSE_COMPANY_PROFILE_FIELDS.map((f) => ({ ...f })),
  ],
  default_sorting_field: 'active_job_count',
};

/**
 * One document per national TITLE-aggregation salary-stats row (lambda/generateSalaryStats.ts).
 * Powers the SRP "In-demand roles" strip: a cheap, ranked, indexed query instead of the
 * DynamoDB Scan `listSalaryStats` does (see utils/search/perform-ssr-role-search.ts).
 */
export const TYPESENSE_SALARY_ROLES_SCHEMA: TypesenseCollectionSchema = {
  name: 'salary_roles',
  fields: [
    { name: 'id', type: 'string' },
    { name: 'slug', type: 'string' },
    { name: 'title', type: 'string' },
    { name: 'sampleSize', type: 'int32' },
  ],
  default_sorting_field: 'sampleSize',
};

const JOB_FIELDS_BASE = [
  { name: 'id', type: 'string' },
  // The job's DynamoDB pk. Indexed (not `id`, which is sha256(url)) so filters can address every
  // document of a job, e.g. `objectID:!=X` in similar jobs and hidden/saved exclusions, and the
  // indexer can delete documents left behind under an older url. See job-object-id-indexed-migration.ts.
  { name: 'objectID', type: 'string', optional: true },
  { name: 'url', type: 'string', index: false },
  { name: 'score', type: 'float' },
  { name: 'lastSeen', type: 'int64' },
  // First time the crawler saw the listing (epoch ms). Optional: docs indexed before the
  // 2026-07 migration lack it until re-seen; alert-window filters must exclude those.
  { name: 'firstSeen', type: 'int64', optional: true },
  { name: 'postedDate', type: 'int64', facet: true },
  { name: 'applicationDeadline', type: 'int64', facet: true, optional: true },
  { name: 'isActive', type: 'bool', optional: true },
  { name: 'title', type: 'string' },
  { name: 'normalisedTitle', type: 'string', facet: true, optional: true },
  { name: 'normalisedRoleLevel', type: 'string[]', facet: true, optional: true },
  { name: 'field', type: 'string[]', facet: true, optional: true },
  { name: 'specialisations', type: 'string[]', optional: true },
  { name: 'educationLevel', type: 'string[]', facet: true, optional: true },
  { name: 'minimumYearsOfExperience', type: 'int32', optional: true },
  { name: 'peopleManagementTier', type: 'string', facet: true, optional: true },
  { name: 'companyId', type: 'string', facet: true, optional: true },
  { name: 'companyName', type: 'string', optional: true, index: false },
  { name: 'logoUrl', type: 'string', optional: true, index: false },
  { name: 'companyIndustry', type: 'string[]', facet: true, optional: true },
  { name: 'brandColour', type: 'string', optional: true, index: false },
  { name: 'heroImage', type: 'string', optional: true, index: false },
  { name: 'employerOverview', type: 'string', optional: true },
  { name: 'location', type: 'string', facet: true },
  // Facetable (unlike `location`, a joined string) so a job valid in multiple cities counts
  // toward each city's facet bucket instead of forming one combined "A, B, C" bucket.
  { name: 'locationList', type: 'string[]', optional: true, facet: true },
  { name: 'geoloc', type: 'geopoint[]', optional: true },
  // Coarse grid-cell bucketing of geoloc (utils/typesense/geo-cell.ts), facetable so the map
  // density view can get a `cell -> count` breakdown covering every matching job in one query
  // instead of a capped page of hits. See job-geo-cell-field-migration.ts.
  { name: 'geoCell', type: 'string[]', optional: true, facet: true },
  // Bracketing tiers added by job-geo-cell-tiers-migration.ts: regionAdmin1 (Finland's 18
  // maakunta regions) and geoCellDistrict (~2km grid) let the map density view drill down
  // through region -> city (geoCell) -> district instead of jumping straight to individual pins.
  { name: 'regionAdmin1', type: 'string[]', optional: true, facet: true },
  { name: 'geoCellDistrict', type: 'string[]', optional: true, facet: true },
  // TEC-482: structured location resolution. locationIds are gazetteer place ids (debug/display,
  // not meant to be faceted directly). remoteEligibilityScopeType/Ref are the structured
  // "Remote - EMEA" replacement. regionTags is the materialized field that actually drives
  // hierarchical + recruiting-region search filtering ("filter APAC -> get onsite Sydney jobs
  // too"), every geo-tree ancestor plus every recruiting region a job belongs to, computed at
  // write time in lambda/enrichJob.ts (unconditional, always computed), not at query time. All
  // optional: absent on jobs enriched before this shipped, or before the gazetteer was seeded.
  { name: 'locationIds', type: 'string[]', optional: true, index: false },
  { name: 'remoteEligibilityScopeType', type: 'string', facet: true, optional: true },
  { name: 'remoteEligibilityRef', type: 'string', optional: true, index: false },
  // TEC-482 follow-up: every remoteScope entry (display-only, not a facet, same as locationIds
  // above), so hit cards/JDP can show all of a job's discrete remote countries instead of only
  // the first one remoteEligibilityScopeType/Ref carries.
  { name: 'remoteEligibilityRefs', type: 'string[]', optional: true, index: false },
  { name: 'regionTags', type: 'string[]', facet: true, optional: true },
  // TEC-482 follow-up: which countries this job's remote eligibility actually reaches (derived
  // from remoteEligibilityScopeType/Ref via computeRemoteEligibleCountryCodes), a deliberately
  // separate, country-level-only dimension from regionTags — see that function's doc comment.
  // Powers a second, distinctly-labeled "N more remote roles open to <country>" result set
  // alongside a place's normal physical-location results, not blended into regionTags/its counts.
  { name: 'remoteEligibleCountryCodes', type: 'string[]', facet: true, optional: true },
  // How precise `geoloc` actually is (verified street address vs. a name+city best-guess vs. the
  // deliberate "don't guess" sea placeholder), set in lambda/enrichJob.ts /
  // mapboxGeocodeFallbackForEnrichedJob.ts. Not user-facing filtering, so index:false, display
  // only (e.g. a map pin styled differently for a verified office vs. an approximate one).
  { name: 'officeLocationPrecision', type: 'string', optional: true, index: false },
  { name: 'remoteness', type: 'string', facet: true, optional: true },
  { name: 'isFinland', type: 'bool', facet: true, optional: true },
  { name: 'jobType', type: 'string', facet: true, optional: true },
  { name: 'description', type: 'string' },
  { name: 'requirements', type: 'string', optional: true },
  { name: 'responsibilities', type: 'string', optional: true },
  { name: 'benefits', type: 'string[]', facet: true, optional: true },
  { name: 'skillsAndTechnologies', type: 'string[]', facet: true, optional: true },
  { name: 'requiredLanguages', type: 'string[]', facet: true, optional: true },
  { name: 'optionalLanguages', type: 'string[]', facet: true, optional: true },
  // Companion array-length fields for every string[] facet above, added by
  // job-array-facet-count-fields-migration. Enable exact "only these values" filtering
  // (`field:=X && fieldCount:=1`), since Typesense filter_by has no array-length/set-equality
  // operator. Keys must mirror ARRAY_FACET_COUNT_FIELD in array-facet-count-fields.ts exactly.
  { name: 'normalisedRoleLevelCount', type: 'int32', facet: true, optional: true },
  { name: 'fieldCount', type: 'int32', facet: true, optional: true },
  { name: 'educationLevelCount', type: 'int32', facet: true, optional: true },
  { name: 'companyIndustryCount', type: 'int32', facet: true, optional: true },
  { name: 'locationListCount', type: 'int32', facet: true, optional: true },
  { name: 'benefitsCount', type: 'int32', facet: true, optional: true },
  { name: 'skillsAndTechnologiesCount', type: 'int32', facet: true, optional: true },
  { name: 'requiredLanguagesCount', type: 'int32', facet: true, optional: true },
  { name: 'optionalLanguagesCount', type: 'int32', facet: true, optional: true },
  { name: 'salaryRangeMin', type: 'int32', facet: true, optional: true },
  { name: 'salaryRangeMax', type: 'int32', facet: true, optional: true },
  // Facetable for the map's salary-transparency lens (see job-salary-is-estimate-facetable-migration).
  { name: 'salaryIsEstimate', type: 'bool', optional: true, facet: true },
  { name: 'salaryEstimateSampleSize', type: 'int32', optional: true, index: false },
  { name: 'salaryPeriod', type: 'string', optional: true },
  { name: 'salaryDetails', type: 'string', optional: true, index: false },
  { name: 'currency', type: 'string', optional: true, index: false },
  { name: 'originalSalaryRangeMin', type: 'int32', optional: true, index: false },
  { name: 'originalSalaryRangeMax', type: 'int32', optional: true, index: false },
  { name: 'originalSalaryPeriod', type: 'string', optional: true, index: false },
  { name: 'originalCurrency', type: 'string', optional: true, index: false },
  { name: 'isOpenApplication', type: 'bool', facet: true, optional: true },
  { name: 'isRecruitmentCompany', type: 'bool', facet: true, optional: true },
  { name: 'isConsultingCompany', type: 'bool', facet: true, optional: true },
  { name: 'isSummerJob', type: 'bool', facet: true, optional: true },
  { name: 'isDriversLicenseRequired', type: 'bool', facet: true, optional: true },
  // TEC-716: the company's ATS vendor (lambda/utils/normalize-ats-vendor.ts), for the Quick apply
  // badge and filter. Faceted so coverage per vendor can be counted.
  { name: 'atsVendor', type: 'string', facet: true, optional: true },
  { name: 'isTyomarkkinatori', type: 'bool', optional: true },
  { name: 'vacationWeeks', type: 'int32', facet: true, optional: true },
  { name: 'companyURL', type: 'string', optional: true, index: false },
  // SRP card B2: display-only fields, never filtered/sorted on, hence index:false throughout.
  // employerGrade: joined in at index time from the company_score row's employerScore
  // (lambda/indexer.ts's applyFreshCompanyProfileFields), mapped through mapEmployerScoreToTier
  // so the raw 0-100 score never reaches the client. salaryBenchmark* are the flattened market
  // salary band computed at enrichment time (lambda/utils/computeSalaryBenchmark.ts); flattened
  // rather than a nested object, matching this schema's existing "no object fields" convention.
  { name: 'employerGrade', type: 'string', optional: true, index: false },
  { name: 'salaryBenchmarkP25', type: 'int32', optional: true, index: false },
  { name: 'salaryBenchmarkP75', type: 'int32', optional: true, index: false },
  { name: 'salaryBenchmarkMedian', type: 'int32', optional: true, index: false },
  { name: 'salaryBenchmarkSampleSize', type: 'int32', optional: true, index: false },
  { name: 'salaryBenchmarkScope', type: 'string', optional: true, index: false },
  // Plus listing history (TEC-744): display-only, set only when the repost history is reliable.
  { name: 'repostChainCount', type: 'int32', optional: true, index: false },
  { name: 'repostOfJobId', type: 'string', optional: true, index: false },
  // Plus round 3 (contract length, shifts and hours filters). Facetable so SRP filters and coverage counts work.
  { name: 'contractLengthMonths', type: 'int32', facet: true, optional: true },
  { name: 'isEveningWork', type: 'bool', facet: true, optional: true },
  { name: 'isWeekendWork', type: 'bool', facet: true, optional: true },
  { name: 'isShiftWork', type: 'bool', facet: true, optional: true },
  { name: 'weeklyHoursMin', type: 'int32', facet: true, optional: true },
  { name: 'weeklyHoursMax', type: 'int32', facet: true, optional: true },
  // R3-27: true only when the ad states no years of experience, so "up to N years" can include those ads.
  { name: 'yearsNotStated', type: 'bool', facet: true, optional: true },
  // Plus round 5 ad signals (apply-now badge, interview weeks, cards, application asks, paperwork, hourly pay).
  { name: 'fillsRolling', type: 'bool', facet: true, optional: true },
  { name: 'interviewsDuringApplication', type: 'bool', facet: true, optional: true },
  { name: 'interviewStartDate', type: 'string', optional: true, index: false },
  { name: 'interviewEndDate', type: 'string', optional: true, index: false },
  { name: 'requiredCards', type: 'string[]', facet: true, optional: true },
  { name: 'advantageCards', type: 'string[]', facet: true, optional: true },
  { name: 'applicationRoute', type: 'string', facet: true, optional: true },
  { name: 'applicationRequires', type: 'string[]', facet: true, optional: true },
  { name: 'paperworkItems', type: 'string[]', facet: true, optional: true },
  { name: 'hourlyPayMin', type: 'float', optional: true },
  { name: 'hourlyPayMax', type: 'float', optional: true },
  { name: 'payAgreementNamed', type: 'bool', facet: true, optional: true },
  { name: 'locale', type: 'string', facet: true },
] as const;

function buildJobsSchema(name: 'jobs_fi' | 'jobs_sv' | 'jobs_en', locale: 'fi' | 'sv' | 'en'): TypesenseCollectionSchema {
  const localizedFields = JOB_FIELDS_BASE.map((field) => {
    if (field.name === 'title' || field.name === 'normalisedTitle' || field.name === 'description') {
      return { ...field, locale };
    }
    return { ...field };
  });

  return {
    name,
    fields: [...localizedFields],
    default_sorting_field: 'score',
    token_separators: ['-', '_', '/'],
  };
}

export const TYPESENSE_JOBS_FI_SCHEMA = buildJobsSchema('jobs_fi', 'fi');
export const TYPESENSE_JOBS_SV_SCHEMA = buildJobsSchema('jobs_sv', 'sv');
export const TYPESENSE_JOBS_EN_SCHEMA = buildJobsSchema('jobs_en', 'en');

/** Creation order: companies first, then jobs, then salary roles. */
export const TYPESENSE_COLLECTION_SCHEMAS: TypesenseCollectionSchema[] = [
  TYPESENSE_COMPANIES_SCHEMA,
  TYPESENSE_JOBS_FI_SCHEMA,
  TYPESENSE_JOBS_SV_SCHEMA,
  TYPESENSE_JOBS_EN_SCHEMA,
  TYPESENSE_SALARY_ROLES_SCHEMA,
];

/**
 * Every facet-enabled field across the jobs and companies schemas, derived (not
 * hand-maintained) so it can never drift from the real schema. Used to validate
 * saved-search / SRP URL query-param keys before treating them as a Typesense
 * filter attribute, see utils/search-router/url-handling.ts. A URL param that isn't a
 * real facet (e.g. a stale/hand-typed `?perks=...`, the real field is `benefits`;
 * `perks` only exists as a job-record data field for match scoring, never as a
 * Typesense facet) must be dropped instead of forwarded to `filter_by`, or every
 * consumer (SRP search, the alert matcher) gets a hard Typesense 400.
 */
export const KNOWN_FACET_ATTRIBUTE_NAMES: ReadonlySet<string> = new Set(
  [...TYPESENSE_JOBS_EN_SCHEMA.fields, ...TYPESENSE_COMPANIES_SCHEMA.fields]
    .filter((field): field is (typeof TYPESENSE_JOBS_EN_SCHEMA.fields)[number] & { facet: true } => field.facet === true)
    .map((field) => field.name)
);

export function getKnownFieldNamesForCollection(collection: string): ReadonlySet<string> | undefined {
  const schema = TYPESENSE_COLLECTION_SCHEMAS.find((s) => s.name === collection);
  return schema ? new Set(schema.fields.map((field) => field.name)) : undefined;
}

const FILTER_OR_SORT_FIELD_NAME_PATTERN = /([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g;

/**
 * Field names referenced in a Typesense `filter_by`/`sort_by` expression that don't exist on
 * `collection`'s schema. Every collection accepts the same free-text `filter_by` shape, so a
 * filter written for one (e.g. `companyId:=X`, jobs-only) silently 404s when sent against
 * another (`salary_roles`, `companies`) instead of failing with a field name to act on — this
 * is what the backoffice "Live search inspector" hits when the collection dropdown and a
 * hand-typed filter fall out of sync. Returns [] when `collection` is unrecognized (schema
 * mismatches on a bad collection name are reported separately by the caller's own validation).
 */
export function findUnknownFilterFields(collection: string, filterOrSortExpression: string): string[] {
  const knownFields = getKnownFieldNamesForCollection(collection);
  if (!knownFields) return [];

  const seen = new Set<string>();
  const unknown: string[] = [];
  for (const match of filterOrSortExpression.matchAll(FILTER_OR_SORT_FIELD_NAME_PATTERN)) {
    const field = match[1];
    if (seen.has(field)) continue;
    seen.add(field);
    if (!knownFields.has(field)) unknown.push(field);
  }
  return unknown;
}

/** Same check as {@link findUnknownFilterFields}, for `facet_by`'s comma-separated field list. */
export function findUnknownFacetFields(collection: string, facetByExpression: string): string[] {
  const knownFields = getKnownFieldNamesForCollection(collection);
  if (!knownFields) return [];

  const seen = new Set<string>();
  const unknown: string[] = [];
  for (const rawField of facetByExpression.split(',')) {
    const field = rawField.trim();
    if (!field || seen.has(field)) continue;
    seen.add(field);
    if (!knownFields.has(field)) unknown.push(field);
  }
  return unknown;
}

export const DEFAULT_TYPESENSE_SEARCH_CONFIG = {
  query_by:
    'normalisedTitle,title,normalisedRoleLevel,location,locationList,remoteness,description,jobType,benefits,companyId,specialisations,companyIndustry,field,educationLevel,skillsAndTechnologies',
  include_fields: TYPESENSE_SRP_INCLUDE_FIELDS,
  per_page: 24,
  drop_tokens_threshold: 1,
  typo_tokens_threshold: 1,
  min_len_1typo: 6,
  min_len_2typo: 12,
} as const;
